import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  BarChart2,
  Users,
  Clock,
  TrendingUp,
  RefreshCw,
  Building2,
  MousePointerClick,
  ChevronDown,
  Globe,
  Smartphone,
  MousePointer2,
} from "lucide-react";

const BUTTON_LABELS = {
  inventory_issue: "Inventory · Issue",
  inventory_purchase: "Inventory · Purchase",
};

const DATE_RANGES = [
  { label: "Today", value: "today" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "Last 90 days", value: "90d" },
];

const CHART_COLORS = [
  "#2563eb", "#7c3aed", "#0891b2", "#059669",
  "#d97706", "#dc2626", "#7c3aed", "#0284c7",
];

function getDateRange(value) {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);

  if (value === "today") return { startDate: start.toISOString(), endDate: end.toISOString() };
  if (value === "7d") { start.setDate(start.getDate() - 6); return { startDate: start.toISOString(), endDate: end.toISOString() }; }
  if (value === "30d") { start.setDate(start.getDate() - 29); return { startDate: start.toISOString(), endDate: end.toISOString() }; }
  if (value === "90d") { start.setDate(start.getDate() - 89); return { startDate: start.toISOString(), endDate: end.toISOString() }; }
  return {};
}

function formatDuration(seconds) {
  if (!seconds || seconds < 1) return "0s";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatCard({ icon, label, value, sub, color = "blue" }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
    teal: "bg-teal-50 text-teal-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
      <div className={`p-2.5 rounded-lg shrink-0 ${colors[color]}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-800 mt-0.5 truncate">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4 animate-pulse">
      <div className="w-10 h-10 bg-gray-100 rounded-lg shrink-0" />
      <div className="flex-1 space-y-2 pt-1">
        <div className="h-3 bg-gray-100 rounded w-24" />
        <div className="h-7 bg-gray-200 rounded w-16" />
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-medium text-gray-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.fill }} className="text-xs">
          {p.name}: <span className="font-semibold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function PageVisitAnalytics() {
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState("");
  const [platform, setPlatform] = useState("");
  const [dateRange, setDateRange] = useState("7d");
  const [stats, setStats] = useState(null);
  const [schoolSummary, setSchoolSummary] = useState([]);
  const [buttonStats, setButtonStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  const fetchSchools = useCallback(async () => {
    setLoadingSchools(true);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.schools);
      setSchools(res.data?.data?.schools ?? res.data?.schools ?? []);
    } catch {
      setSchools([]);
    } finally {
      setLoadingSchools(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { startDate, endDate } = getDateRange(dateRange);
      const params = new URLSearchParams({ startDate, endDate });
      if (selectedSchool) params.set("schoolId", selectedSchool);
      if (platform) params.set("platform", platform);
      const res = await axiosInstance.get(`${API_ENDPOINTS.pageVisitStats}?${params}`);
      setStats(res.data?.data ?? null);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [selectedSchool, platform, dateRange]);

  const fetchSchoolSummary = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { startDate, endDate } = getDateRange(dateRange);
      const params = new URLSearchParams({ startDate, endDate });
      const res = await axiosInstance.get(`${API_ENDPOINTS.pageVisitSchools}?${params}`);
      setSchoolSummary(res.data?.data?.schoolStats ?? []);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load school summary");
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const fetchButtonStats = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { startDate, endDate } = getDateRange(dateRange);
      const params = new URLSearchParams({ startDate, endDate });
      if (selectedSchool) params.set("schoolId", selectedSchool);
      const res = await axiosInstance.get(`${API_ENDPOINTS.buttonClickStats}?${params}`);
      setButtonStats(res.data?.data ?? null);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load button records");
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedSchool]);

  useEffect(() => { fetchSchools(); }, [fetchSchools]);

  useEffect(() => {
    if (activeTab === "overview") fetchStats();
    else if (activeTab === "schools") fetchSchoolSummary();
    else fetchButtonStats();
  }, [activeTab, fetchStats, fetchSchoolSummary, fetchButtonStats]);

  const summary = stats?.summary ?? { totalVisits: 0, uniqueUsers: 0, avgDuration: 0 };
  const topPage = stats?.pageStats?.[0];

  const platformStats = stats?.platformStats ?? [];
  const webStats = platformStats.find((p) => p.platform === "web") ?? { visits: 0, uniqueUsers: 0, avgDuration: 0 };
  const appStats = platformStats.find((p) => p.platform === "app") ?? { visits: 0, uniqueUsers: 0, avgDuration: 0 };
  const platformTotal = webStats.visits + appStats.visits || 1;
  const webPct = Math.round((webStats.visits / platformTotal) * 100);
  const appPct = 100 - webPct;

  const pageChartData = (stats?.pageStats ?? []).slice(0, 8).map((p) => ({
    name: p.pageName?.length > 14 ? p.pageName.slice(0, 14) + "…" : (p.pageName || p.page),
    visits: p.visits,
    users: p.uniqueUsers,
  }));

  const schoolChartData = schools.length
    ? schoolSummary.slice(0, 8).map((row) => {
        const school = schools.find((s) => s._id === row.schoolId);
        const name = school?.name || school?.schoolName || "Unknown";
        return {
          name: name.length > 12 ? name.slice(0, 12) + "…" : name,
          visits: row.totalVisits,
          users: row.uniqueUsers,
        };
      })
    : [];

  const handleRefresh = () => {
    if (activeTab === "overview") fetchStats();
    else if (activeTab === "schools") fetchSchoolSummary();
    else fetchButtonStats();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Page Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">Track which pages users visit and how long they stay</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-50 self-start transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          { id: "overview", label: "Overview" },
          { id: "schools", label: "By School" },
          { id: "buttons", label: "Button Records" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {DATE_RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setDateRange(r.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                dateRange === r.value
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {(activeTab === "overview" || activeTab === "buttons") && (
          <div className="relative">
            <select
              value={selectedSchool}
              onChange={(e) => setSelectedSchool(e.target.value)}
              disabled={loadingSchools}
              className="appearance-none pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="">All Schools</option>
              {schools.map((s) => (
                <option key={s._id} value={s._id}>{s.name || s.schoolName}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        )}

        {activeTab === "overview" && (
          <div className="relative">
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Web + App</option>
              <option value="web">Web only</option>
              <option value="app">App only</option>
            </select>
            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 text-sm rounded">
          {error}
        </div>
      )}

      {/* ── OVERVIEW TAB ── */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
            ) : (
              <>
                <StatCard
                  icon={<MousePointerClick className="w-5 h-5" />}
                  label="Total Visits"
                  value={summary.totalVisits.toLocaleString()}
                  color="blue"
                />
                <StatCard
                  icon={<Users className="w-5 h-5" />}
                  label="Unique Users"
                  value={summary.uniqueUsers.toLocaleString()}
                  color="purple"
                />
                <StatCard
                  icon={<Clock className="w-5 h-5" />}
                  label="Avg Time on Page"
                  value={formatDuration(summary.avgDuration)}
                  color="teal"
                />
                <StatCard
                  icon={<TrendingUp className="w-5 h-5" />}
                  label="Most Visited"
                  value={topPage?.pageName ?? "—"}
                  sub={topPage ? `${topPage.visits} visits` : undefined}
                  color="amber"
                />
              </>
            )}
          </div>

          {/* Web vs App breakdown */}
          {!loading && platformTotal > 1 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Web vs App</h2>
              <div className="flex rounded-full h-2.5 overflow-hidden bg-gray-100 mb-3">
                <div className="bg-blue-500" style={{ width: `${webPct}%` }} />
                <div className="bg-violet-500" style={{ width: `${appPct}%` }} />
              </div>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-lg bg-blue-50 text-blue-600"><Globe className="w-4 h-4" /></div>
                  <div>
                    <p className="text-xs text-gray-500">Web</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {webStats.visits.toLocaleString()} visits <span className="text-gray-400 font-normal">({webPct}%)</span>
                    </p>
                    <p className="text-xs text-gray-400">{webStats.uniqueUsers} users · {formatDuration(webStats.avgDuration)} avg</p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-lg bg-violet-50 text-violet-600"><Smartphone className="w-4 h-4" /></div>
                  <div>
                    <p className="text-xs text-gray-500">App</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {appStats.visits.toLocaleString()} visits <span className="text-gray-400 font-normal">({appPct}%)</span>
                    </p>
                    <p className="text-xs text-gray-400">{appStats.uniqueUsers} users · {formatDuration(appStats.avgDuration)} avg</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Top Pages Chart */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-gray-400" />
              <h2 className="text-base font-semibold text-gray-800">Top Pages — Visits</h2>
            </div>
            {loading ? (
              <div className="h-64 flex items-center justify-center">
                <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full" />
              </div>
            ) : !pageChartData.length ? (
              <div className="h-64 flex items-center justify-center text-sm text-gray-400">
                No data for selected period
              </div>
            ) : (
              <div className="px-4 pb-4 pt-2">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={pageChartData} margin={{ top: 4, right: 16, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      angle={-25}
                      textAnchor="end"
                    />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                    <Bar dataKey="visits" name="Visits" radius={[4, 4, 0, 0]}>
                      {pageChartData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Top Pages Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-800">Page Breakdown</h2>
            </div>
            {loading ? (
              <div className="divide-y divide-gray-100">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="px-6 py-4 flex items-center gap-4 animate-pulse">
                    <div className="w-5 h-3 bg-gray-100 rounded" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3.5 bg-gray-200 rounded w-40" />
                      <div className="h-2.5 bg-gray-100 rounded w-24" />
                    </div>
                    <div className="h-3 bg-gray-100 rounded w-12" />
                    <div className="h-3 bg-gray-100 rounded w-12" />
                    <div className="h-3 bg-gray-100 rounded w-14" />
                  </div>
                ))}
              </div>
            ) : !stats?.pageStats?.length ? (
              <div className="px-6 py-12 text-center text-sm text-gray-400">No data for selected period</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                    <tr>
                      <th className="px-6 py-3 text-left font-medium">#</th>
                      <th className="px-6 py-3 text-left font-medium">Page</th>
                      <th className="px-6 py-3 text-right font-medium">Visits</th>
                      <th className="px-6 py-3 text-right font-medium">Unique Users</th>
                      <th className="px-6 py-3 text-right font-medium">Avg Duration</th>
                      <th className="px-6 py-3 text-left font-medium w-40">Share</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {stats.pageStats.map((row, i) => {
                      const maxVisits = stats.pageStats[0]?.visits || 1;
                      const pct = Math.round((row.visits / maxVisits) * 100);
                      return (
                        <tr key={row.page} className="hover:bg-gray-50">
                          <td className="px-6 py-3 text-xs text-gray-400">{i + 1}</td>
                          <td className="px-6 py-3">
                            <p className="font-medium text-gray-800">{row.pageName}</p>
                            <p className="text-xs text-gray-400 font-mono">{row.page}</p>
                          </td>
                          <td className="px-6 py-3 text-right font-semibold text-gray-700">{row.visits}</td>
                          <td className="px-6 py-3 text-right text-gray-500">{row.uniqueUsers}</td>
                          <td className="px-6 py-3 text-right text-gray-500">{formatDuration(row.avgDuration)}</td>
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                                <div
                                  className="h-1.5 rounded-full bg-blue-500"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-400 w-8 text-right">{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-800">Recent Activity</h2>
              <p className="text-xs text-gray-400 mt-0.5">Last 30 visits</p>
            </div>
            {loading ? (
              <div className="divide-y divide-gray-100">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="px-6 py-3 flex items-center justify-between animate-pulse">
                    <div className="space-y-1.5">
                      <div className="h-3.5 bg-gray-200 rounded w-32" />
                      <div className="h-2.5 bg-gray-100 rounded w-20" />
                    </div>
                    <div className="text-right space-y-1.5">
                      <div className="h-3 bg-gray-100 rounded w-10" />
                      <div className="h-2.5 bg-gray-100 rounded w-20" />
                    </div>
                  </div>
                ))}
              </div>
            ) : !stats?.recent?.length ? (
              <div className="px-6 py-12 text-center text-sm text-gray-400">No recent activity</div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                {stats.recent.map((visit) => (
                  <div key={visit._id} className="px-6 py-3 flex items-center justify-between hover:bg-gray-50">
                    <div className="min-w-0 mr-4">
                      <p className="text-sm font-medium text-gray-800 truncate">{visit.pageName}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-500 font-medium capitalize">
                          {visit.role}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                          visit.platform === "app" ? "bg-violet-50 text-violet-600" : "bg-blue-50 text-blue-600"
                        }`}>
                          {visit.platform === "app" ? <Smartphone className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                          {visit.platform === "app" ? "App" : "Web"}
                        </span>
                        <span className="text-xs text-gray-400 font-mono truncate">{visit.page}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs font-medium text-gray-600">{formatDuration(visit.duration)}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{formatTime(visit.enteredAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── BY SCHOOL TAB ── */}
      {activeTab === "schools" && (
        <div className="space-y-6">
          {/* School Chart */}
          {!loading && schoolChartData.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-gray-400" />
                <h2 className="text-base font-semibold text-gray-800">Visits by School</h2>
              </div>
              <div className="px-4 pb-4 pt-2">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={schoolChartData} margin={{ top: 4, right: 16, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      angle={-25}
                      textAnchor="end"
                    />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                    <Bar dataKey="visits" name="Visits" radius={[4, 4, 0, 0]}>
                      {schoolChartData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* School Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-gray-400" />
              <h2 className="text-base font-semibold text-gray-800">School-wise Activity</h2>
            </div>
            {loading ? (
              <div className="divide-y divide-gray-100">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="px-6 py-4 flex items-center gap-4 animate-pulse">
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3.5 bg-gray-200 rounded w-40" />
                      <div className="h-2.5 bg-gray-100 rounded w-24" />
                    </div>
                    <div className="h-3 bg-gray-100 rounded w-12" />
                    <div className="h-3 bg-gray-100 rounded w-12" />
                    <div className="h-3 bg-gray-100 rounded w-12" />
                    <div className="h-3 bg-gray-100 rounded w-24" />
                  </div>
                ))}
              </div>
            ) : !schoolSummary.length ? (
              <div className="px-6 py-12 text-center text-sm text-gray-400">No data for selected period</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                    <tr>
                      <th className="px-6 py-3 text-left font-medium">School</th>
                      <th className="px-6 py-3 text-right font-medium">Total Visits</th>
                      <th className="px-6 py-3 text-right font-medium">Unique Users</th>
                      <th className="px-6 py-3 text-right font-medium">Avg Duration</th>
                      <th className="px-6 py-3 text-right font-medium">Last Visit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {schoolSummary.map((row, i) => {
                      const school = schools.find((s) => s._id === row.schoolId);
                      const maxVisits = schoolSummary[0]?.totalVisits || 1;
                      const pct = Math.round((row.totalVisits / maxVisits) * 100);
                      return (
                        <tr key={row.schoolId} className="hover:bg-gray-50">
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                              />
                              <div>
                                <p className="font-medium text-gray-800">
                                  {school?.name || school?.schoolName || "Unknown School"}
                                </p>
                                <p className="text-xs text-gray-400 font-mono">{row.schoolId}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-20 bg-gray-100 rounded-full h-1.5 hidden sm:block">
                                <div
                                  className="h-1.5 rounded-full"
                                  style={{
                                    width: `${pct}%`,
                                    backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                                  }}
                                />
                              </div>
                              <span className="font-semibold text-gray-700">{row.totalVisits}</span>
                            </div>
                          </td>
                          <td className="px-6 py-3 text-right text-gray-500">{row.uniqueUsers}</td>
                          <td className="px-6 py-3 text-right text-gray-500">{formatDuration(row.avgDuration)}</td>
                          <td className="px-6 py-3 text-right text-gray-400 text-xs">{formatTime(row.lastVisit)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── BUTTON RECORDS TAB ── */}
      {activeTab === "buttons" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {loading ? (
              Array.from({ length: 2 }).map((_, i) => <SkeletonCard key={i} />)
            ) : (
              Object.entries(BUTTON_LABELS).map(([key, label]) => {
                const row = buttonStats?.byButton?.find((b) => b.button === key);
                return (
                  <StatCard
                    key={key}
                    icon={<MousePointer2 className="w-5 h-5" />}
                    label={label}
                    value={(row?.clicks ?? 0).toLocaleString()}
                    sub={`${row?.uniqueUsers ?? 0} unique user${(row?.uniqueUsers ?? 0) === 1 ? "" : "s"}`}
                    color="blue"
                  />
                );
              })
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <MousePointer2 className="w-5 h-5 text-gray-400" />
                <h2 className="text-base font-semibold text-gray-800">Who Clicked What</h2>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">Inventory Issue / Purchase button presses — app only</p>
            </div>
            {loading ? (
              <div className="divide-y divide-gray-100">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="px-6 py-4 flex items-center gap-4 animate-pulse">
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3.5 bg-gray-200 rounded w-40" />
                      <div className="h-2.5 bg-gray-100 rounded w-24" />
                    </div>
                    <div className="h-3 bg-gray-100 rounded w-16" />
                    <div className="h-3 bg-gray-100 rounded w-12" />
                    <div className="h-3 bg-gray-100 rounded w-24" />
                  </div>
                ))}
              </div>
            ) : !buttonStats?.byUser?.length ? (
              <div className="px-6 py-12 text-center text-sm text-gray-400">No button clicks recorded for this period</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                    <tr>
                      <th className="px-6 py-3 text-left font-medium">User</th>
                      <th className="px-6 py-3 text-left font-medium">School</th>
                      <th className="px-6 py-3 text-left font-medium">Button</th>
                      <th className="px-6 py-3 text-right font-medium">Clicks</th>
                      <th className="px-6 py-3 text-right font-medium">Last Clicked</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {buttonStats.byUser.map((row, i) => {
                      const school = schools.find((s) => s._id === row.schoolId);
                      return (
                        <tr key={`${row.userId}-${row.button}-${i}`} className="hover:bg-gray-50">
                          <td className="px-6 py-3">
                            <p className="font-medium text-gray-800">{row.userName || "Unknown"}</p>
                            <p className="text-xs text-gray-400 capitalize">{row.role}</p>
                          </td>
                          <td className="px-6 py-3 text-gray-600">{school?.name || school?.schoolName || "—"}</td>
                          <td className="px-6 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-600">
                              {BUTTON_LABELS[row.button] || row.button}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <span className={`font-semibold ${row.clicks > 1 ? "text-amber-600" : "text-gray-700"}`}>
                              {row.clicks}
                            </span>
                            {row.clicks > 1 && (
                              <span className="ml-1.5 text-[10px] text-amber-500 font-normal">multi-tap</span>
                            )}
                          </td>
                          <td className="px-6 py-3 text-right text-gray-400 text-xs">{formatTime(row.lastClicked)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-800">Recent Clicks</h2>
              <p className="text-xs text-gray-400 mt-0.5">Last 30 button presses</p>
            </div>
            {!buttonStats?.recent?.length ? (
              <div className="px-6 py-12 text-center text-sm text-gray-400">No recent clicks</div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                {buttonStats.recent.map((click) => {
                  const school = schools.find((s) => s._id === click.schoolId);
                  return (
                    <div key={click._id} className="px-6 py-3 flex items-center justify-between hover:bg-gray-50">
                      <div className="min-w-0 mr-4">
                        <p className="text-sm font-medium text-gray-800 truncate">{click.userName || "Unknown"}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-500 font-medium capitalize">
                            {click.role}
                          </span>
                          <span className="text-xs text-gray-400 truncate">
                            {school?.name || school?.schoolName || "—"} · {BUTTON_LABELS[click.button] || click.button}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 shrink-0">{formatTime(click.clickedAt)}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
